/**
 * @fileoverview Preset definitions for `DashboardThemeEditor` (`@firesquirrel/ui-admin`).
 * @module ui-admin/components/DashboardThemeEditor/dashboardThemePresets
 *
 * Responsibilities: - Define the 5 built-in dashboard theme presets (`brand`, `slate`, `light`,
 *                     `dark`, `high-contrast`) and helper to resolve a `DashboardThemeConfig`
 *                     to concrete visual tokens.
 * Dependencies:     - @firesquirrel/local-db (DashboardThemeConfig)
 * Architecture:     - Pure static presets and resolution helper; zero data access.
 * Notes:            - Used by `DashboardThemeEditor` and dashboard preview/view rendering.
 */

import type { DashboardThemeConfig } from '@firesquirrel/local-db';

/**
 * @contract DashboardThemePreset
 * Purpose: Metadata and default colors for a named dashboard theme preset.
 * Fields:  id, label, description, config
 * Rules:   id matches `DashboardThemeConfig['presetId']`.
 * Sync:    N/A.
 */
export interface DashboardThemePreset {
  id: NonNullable<DashboardThemeConfig['presetId']>;
  label: string;
  description: string;
  config: Required<Omit<DashboardThemeConfig, 'presetId'>>;
}

export const DASHBOARD_THEME_PRESETS: DashboardThemePreset[] = [
  {
    id: 'brand',
    label: 'Brand',
    description: 'Warm slate background with department brand accent',
    config: {
      pageBg: '#f8fafc',
      cardBg: '#ffffff',
      cardBorder: '#e2e8f0',
      accentColor: '#ea580c',
      textPrimary: '#0f172a',
      fontFamily: 'Inter, system-ui, sans-serif',
    },
  },
  {
    id: 'slate',
    label: 'Slate',
    description: 'Neutral slate surfaces with cool blue accent',
    config: {
      pageBg: '#f1f5f9',
      cardBg: '#ffffff',
      cardBorder: '#cbd5e1',
      accentColor: '#2563eb',
      textPrimary: '#1e293b',
      fontFamily: 'Inter, system-ui, sans-serif',
    },
  },
  {
    id: 'light',
    label: 'Clean Light',
    description: 'High-clarity white canvas for printed or wall-mounted displays',
    config: {
      pageBg: '#ffffff',
      cardBg: '#ffffff',
      cardBorder: '#e5e7eb',
      accentColor: '#0284c7',
      textPrimary: '#111827',
      fontFamily: 'Inter, system-ui, sans-serif',
    },
  },
  {
    id: 'dark',
    label: 'Command Center Dark',
    description: 'Low-glare dark theme for 24/7 operations center screens',
    config: {
      pageBg: '#0f172a',
      cardBg: '#1e293b',
      cardBorder: '#334155',
      accentColor: '#f97316',
      textPrimary: '#f8fafc',
      fontFamily: 'Inter, system-ui, sans-serif',
    },
  },
  {
    id: 'high-contrast',
    label: 'High Contrast',
    description: 'Maximum contrast borders and typography for accessibility',
    config: {
      pageBg: '#ffffff',
      cardBg: '#ffffff',
      cardBorder: '#0f172a',
      accentColor: '#b91c1c',
      textPrimary: '#000000',
      fontFamily: 'Inter, system-ui, sans-serif',
    },
  },
];

/**
 * Resolve a `DashboardThemeConfig` into concrete color/font tokens, falling back to the
 * matching preset or the `'brand'` preset.
 *
 * @process resolveDashboardTheme
 * Steps:   1. Find preset matching `theme?.presetId` (default `'brand'`).
 *          2. Merge explicit overrides from `theme`.
 * Inputs:  theme?: DashboardThemeConfig.
 * Outputs: Required<DashboardThemeConfig>.
 * Failure Handling: Pure function; never throws.
 * Idempotency: REQUIRED.
 */
export function resolveDashboardTheme(theme?: DashboardThemeConfig): Required<DashboardThemeConfig> {
  const preset =
    DASHBOARD_THEME_PRESETS.find((p) => p.id === (theme?.presetId ?? 'brand')) ??
    DASHBOARD_THEME_PRESETS[0];
  return {
    presetId: theme?.presetId ?? preset.id,
    pageBg: theme?.pageBg ?? preset.config.pageBg,
    cardBg: theme?.cardBg ?? preset.config.cardBg,
    cardBorder: theme?.cardBorder ?? preset.config.cardBorder,
    accentColor: theme?.accentColor ?? preset.config.accentColor,
    textPrimary: theme?.textPrimary ?? preset.config.textPrimary,
    fontFamily: theme?.fontFamily ?? preset.config.fontFamily,
  };
}
